/**
 * La coquille d'un écran de file — au clavier avant la souris.
 *
 * `UX-DR25` : sur les écrans de traitement en file du back-office, densité
 * d'information et **vitesse de saisie au clavier** priment sur l'esthétique.
 * C'est le seul écran du projet dont la performance ergonomique a un coût
 * salarial direct — l'objectif est de moins de quatre-vingt-dix secondes par
 * dossier — et une file qui oblige à lâcher le clavier pour la souris le rate.
 *
 * Le parcours complet tient en trois touches, plus les raccourcis de décision :
 *
 *  - **Tab** descend la file, ligne à ligne, et le focus reste visible ;
 *  - **Entrée** ouvre la ligne sélectionnée et **porte le focus** dans le
 *    panneau, sans quoi la touche suivante retournerait dans la file ;
 *  - **Échap** referme le panneau et **rend le focus à la ligne d'où l'on
 *    vient**, sans quoi l'opérateur repartirait du haut de la file ;
 *  - **une touche par décision** — déclarée dans `decisions`, annoncée dans le
 *    libellé du bouton — rend la décision sans toucher la souris. Les boutons
 *    existent aussi, et se traversent au Tab : le raccourci est un accélérateur,
 *    pas la seule voie.
 *
 * **Après une décision, le focus va au dossier suivant**, et non à celui qu'on
 * vient de traiter. C'est la différence entre traiter une file et traiter un
 * dossier : rendre le focus au dossier décidé obligerait à un Tab de plus par
 * dossier, soit, sur une journée, plusieurs minutes de gestes inutiles. Échap,
 * lui, revient bien à la ligne d'où l'on vient — refermer sans décider n'avance
 * pas dans la file.
 *
 * La décision est **annoncée au lecteur d'écran** par une région vivante :
 * sans elle, un opérateur non voyant ne saurait pas si sa touche a été prise en
 * compte, et appuierait deux fois.
 *
 * **Aucun écran métier.** Cette coquille ne connaît ni dossier, ni client, ni
 * transaction : elle reçoit des colonnes, des lignes déjà mises en forme, et
 * un rendu de détail. Les epics 1 à 14 lui donneront leur contenu.
 */

import { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';

import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import type { ColumnDef } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

/**
 * L'**emphase** d'une colonne — ce qui distingue une cellule d'une autre.
 *
 * La maquette ne peint pas ses cellules pareil, et ce n'est pas de la
 * décoration : sur six lignes d'un journal, une adresse IP en gris est ce qui
 * permet à l'œil de descendre la colonne des actions sans être arrêté. Ses
 * fabricants de cellule sont au nombre de trois, déclarés juste au-dessus de
 * `boDefs` dans le fichier `.dc.html`, et ce sont eux que ces quatre valeurs
 * reprennent :
 *
 *  - `numeric` — le `NUM` du dessin : chiffres tabulaires et graisse 600. Une
 *    colonne de nombres qui ne s'aligne pas ne se compare pas ;
 *  - `strong` — le `T(…, 'font-weight:600')` du dessin : la colonne qui NOMME
 *    la ligne, celle qu'on lit en premier ;
 *  - `muted` — le `MUT` du dessin, `var(--ts)` : ce qui doit être là sans
 *    retenir le regard ;
 *  - `plain` — le `T` nu : le corps du texte, et le défaut.
 *
 * Les pastilles d'état du dessin — `OK`, `WARN`, `BAD` — ne sont **pas**
 * reprises : elles portent un jugement (vert, orange, rouge) sur des codes que
 * le serveur rend sans le porter. Un état « OUVERT » peint en rouge dirait que
 * l'écart est grave ; rien ne le dit.
 */
export type QueueEmphasis = 'plain' | 'strong' | 'numeric' | 'muted';

/** Une colonne de la file : un identifiant stable, sa clé de traduction, son emphase. */
export interface QueueColumn {
  readonly id: string;
  readonly labelKey: string;
  /** Comment cette colonne se peint. Absente, c'est `plain`. */
  readonly emphasis?: QueueEmphasis;
  /**
   * Ce que l'**en-tête** écrit, quand ce n'est pas la traduction de `labelKey`.
   *
   * Le dessin de la revue KYC coiffe sa file d'un bandeau qui porte un titre
   * **et un compte** — « FILE D'ATTENTE · 18 » —, et le compte est une donnée
   * servie : il change à chaque lecture, et aucune clé de traduction ne peut le
   * porter.
   *
   * Il ne remplace `labelKey` **que dans l'en-tête**. Le résumé du panneau
   * continue de nommer ses lignes par `labelKey` : un fait intitulé « File
   * d'attente · 18 » y dirait le nombre de dossiers en file au-dessus du nom
   * d'un client, ce qui n'a aucun sens.
   */
  readonly headerLabel?: string;
}

/** Une ligne de la file, déjà mise en forme par l'appelant. */
export interface QueueEntry {
  /** Identifiant technique, jamais affiché. */
  readonly id: string;
  /**
   * Référence lisible, citée dans le libellé accessible de la ligne. Ce n'est
   * pas du texte à traduire : c'est une donnée.
   */
  readonly reference: string;
  /** Les valeurs, dans l'ordre des colonnes. */
  readonly values: readonly string[];
  /**
   * Ce que la **table** rend, colonne par colonne, quand une chaîne ne suffit
   * pas à porter la forme dessinée.
   *
   * Une ligne de la file de revue KYC n'est pas une cellule de texte : le dessin
   * y empile un **nom** et, sous lui, une ligne de contexte plus petite et plus
   * pâle. Deux corps, deux graisses, deux couleurs — et chacune des deux lignes
   * doit se couper par une ellipse plutôt que passer à la ligne, ce qu'une
   * chaîne unique rendue en `pre-line` ne sait pas faire : elle n'a qu'un seul
   * style, et son ellipse ne s'applique qu'à la dernière ligne.
   *
   * Elle suit l'ordre des colonnes ; une case absente retombe sur `values`.
   * `values` reste donc la **vérité textuelle** de la ligne — c'est elle que
   * l'en-tête du panneau, sa pastille et son résumé lisent —, et celle-ci n'en
   * est que le rendu.
   */
  readonly cells?: readonly ReactNode[];
  /**
   * Ce que le **panneau** montre, colonne par colonne, quand la colonne
   * tronque.
   *
   * Le résumé du panneau est bâti à partir des colonnes — c'est le point de
   * `head.map((h, i) => ({ label: h, value: row.cells[i] }))` du dessin, et
   * c'est ce qui fait qu'une colonne ajoutée à la table apparaît au panneau
   * sans que personne y pense. Mais une cellule de table est **tronquée par la
   * largeur de sa colonne** : le journal d'audit y écrit `14:28` et les huit
   * premiers caractères d'un `UUID`.
   *
   * Cette liste, quand elle est fournie, est ce que le panneau montre à la
   * place — l'instant exact dans un `<time dateTime>`, l'identifiant entier.
   * Elle suit l'ordre des colonnes ; une case absente retombe sur `values`.
   * Sans elle, rendre le panneau dérivable des colonnes aurait coûté la
   * garantie inverse : « la ligne est tronquée par la largeur de sa colonne,
   * le panneau ne l'est pas ».
   */
  readonly fullValues?: readonly ReactNode[];
}

/**
 * Une décision que l'opérateur peut rendre sur le dossier ouvert.
 *
 * `shortcut` est la **touche** qui la rend sans passer par le bouton. Elle est
 * comparée en minuscule : un opérateur qui a laissé la majuscule active ne doit
 * pas perdre ses raccourcis.
 */
export interface QueueDecision {
  readonly id: string;
  readonly labelKey: string;
  /**
   * La touche, quand il y en a une. Le dessin n'en donne qu'à sa PREMIÈRE
   * commande — `['Exporter la sélection', 'download', 'E']` —, les deux autres
   * portent une chaîne vide. Une commande sans touche reste un bouton, et se
   * traverse au Tab comme les autres.
   */
  readonly shortcut?: string;
  /**
   * Cette commande **dispose-t-elle du dossier** ?
   *
   * Par défaut oui : c'est ce qu'est une décision — le dossier est traité, la
   * file avance, et le focus va au suivant. C'est ce qui permet d'enchaîner
   * sans un Tab de plus par dossier.
   *
   * Les commandes du journal d'audit, elles, ne décident de rien : exporter la
   * page ou filtrer sur l'acteur d'une ligne ne traite aucun dossier. Refermer
   * le panneau et sauter à la ligne suivante y serait une perte — l'auditeur
   * regardait CETTE entrée, et c'est pour elle qu'il a lancé la commande.
   */
  readonly advances?: boolean;
}

/** Ce que la coquille attend. */
export interface QueueShellProps {
  readonly columns: readonly QueueColumn[];
  readonly entries: readonly QueueEntry[];
  /** Le contenu du panneau de détail, rendu par l'appelant. */
  readonly renderDetail?: (entry: QueueEntry) => ReactNode;
  /**
   * Ce que l'appelant fait de l'**ouverture** d'une ligne, si quelque chose.
   *
   * Appelé une fois par ouverture, au clic comme à la touche Entrée — les deux
   * chemins passent par la même fonction, sans quoi le clavier et la souris
   * n'auraient pas le même effet, ce que cette coquille existe pour empêcher.
   *
   * Le module Clients s'en sert pour ce qu'aucun autre appelant ne demandait :
   * ouvrir une fiche est un acte **journalisé** (`EX-BO-004`), et la trace
   * s'écrit du côté serveur, à l'appel. Sans ce point d'accroche, l'écran
   * dépilerait la ligne déjà reçue et afficherait une fiche que le journal ne
   * mentionne pas.
   */
  readonly onOpen?: (entry: QueueEntry) => void;
  /**
   * Les décisions offertes sur le dossier ouvert. Vide par défaut : une file
   * de consultation n'en a aucune.
   */
  readonly decisions?: readonly QueueDecision[];
  /** Ce que la coquille fait d'une décision. Elle ne décide de rien elle-même. */
  readonly onDecide?: (entry: QueueEntry, decisionId: string) => void;
  /**
   * La **première ligne s'ouvre d'elle-même**, sans attendre un geste.
   *
   * Faux par défaut, et il faut que ce le soit : ouvrir une ligne est un acte
   * **journalisé** (`EX-BO-004`) sur les files qui montrent de la donnée
   * personnelle. Une fiche client qui s'ouvrirait toute seule à l'affichage de
   * la liste écrirait au journal d'audit une consultation que personne n'a
   * demandée — et l'agent devrait s'en expliquer.
   *
   * La revue KYC, elle, le demande : le dessin ne montre **jamais** un côté
   * droit vide, et l'écran livré ouvrait sur une page blanche à côté d'une
   * liste. Le dossier retenu est le **premier de la file**, qui est déjà celui
   * que l'analyste doit traiter — la file est ordonnée par priorité puis par
   * ancienneté, côté serveur. Sur cet écran, ouvrir le dossier suivant est le
   * travail lui-même, et non une consultation gratuite.
   *
   * **Le focus ne bouge pas.** Une ouverture que personne n'a demandée ne
   * déplace pas le curseur : un opérateur au clavier se retrouverait dans un
   * panneau où il n'est pas allé, et un lecteur d'écran annoncerait un dossier
   * au milieu d'une phrase.
   *
   * **Et refermer referme pour de bon.** Échap ne rouvre pas ce qu'on vient de
   * fermer ; sans cela la touche n'aurait aucun effet visible.
   */
  readonly openFirstByDefault?: boolean;
  /**
   * Le **pied de la carte de la table** — le compte, et la pagination.
   *
   * Le dessin ne le met pas SOUS la table : il le met dedans, dans la même
   * carte bordée, séparé par un filet — `padding:8px 14px; border-top:1px solid
   * var(--bsub)`. Le livré posait le compte au-dessus de la table et la
   * pagination en dessous, hors de la carte, et la page se lisait comme trois
   * blocs empilés au lieu d'un tableau.
   *
   * La coquille ne sait pas paginer, et n'a pas à l'apprendre : elle reçoit un
   * rendu et lui donne sa place. Une file sans pagination n'en passe aucun.
   */
  readonly footer?: ReactNode;
}

/**
 * La file et son panneau de détail.
 *
 * Le panneau n'est pas une boîte de dialogue modale : l'opérateur doit pouvoir
 * lire la file en même temps que le dossier ouvert. C'est pourquoi Échap est
 * traité sur le conteneur entier, et non piégé dans le panneau.
 */
export function QueueShell({
  columns,
  entries,
  renderDetail,
  onOpen,
  decisions = [],
  onDecide,
  openFirstByDefault = false,
  footer,
}: QueueShellProps): ReactNode {
  const { t } = useTranslation();
  const [openId, setOpenId] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const rowRefs = useRef(new Map<string, HTMLTableRowElement>());
  const panneau = useRef<HTMLElement | null>(null);
  const detailId = useId();
  /**
   * L'opérateur a-t-il **refermé lui-même** le panneau ?
   *
   * Sans ce souvenir, l'ouverture par défaut rouvrirait aussitôt ce qu'Échap
   * vient de fermer, et la touche n'aurait aucun effet visible. Une décision, en
   * revanche, ne le pose pas : elle libère le dossier suivant, et le dessin dit
   * lui-même qu'il « s'ouvre automatiquement ».
   */
  const fermeParLOperateur = useRef(false);
  /**
   * Les lignes que la coquille a **déjà ouvertes**.
   *
   * L'ouverture par défaut ne rejoue pas ce qui a été ouvert une fois. Sans ce
   * souvenir, elle rouvrirait le dossier qu'on vient de décider : la décision
   * referme le panneau, et la file n'est relue qu'ensuite — pendant ce
   * battement, la première ligne est encore celle qui vient d'être traitée.
   */
  const dejaOuvertes = useRef(new Set<string>());

  const definitions: ColumnDef<QueueEntry>[] = columns.map((column, index) => ({
    id: column.id,
    header: () => column.headerLabel ?? t(column.labelKey),
    // `cells` d'abord, `values` ensuite : la première porte la FORME que le
    // dessin demande, la seconde reste la vérité textuelle que le panneau lit.
    cell: (contexte) =>
      contexte.row.original.cells?.[index] ?? contexte.row.original.values[index] ?? '',
  }));

  const table = useReactTable<QueueEntry>({
    data: entries as QueueEntry[],
    columns: definitions,
    getCoreRowModel: getCoreRowModel(),
  });

  const open = useCallback(
    (id: string, porterLeFocus = true) => {
      // **Rouvrir le dossier déjà ouvert n'est pas une ouverture.** Elle
      // écrirait au journal d'audit une seconde consultation pour un regard qui
      // n'a pas eu lieu, et redemanderait au serveur les octets d'une pièce
      // d'identité déjà en main. Le focus, lui, part quand même : c'est ce que
      // l'opérateur a demandé en appuyant sur Entrée.
      if (id === openId) {
        if (porterLeFocus) queueMicrotask(() => panneau.current?.focus());
        return;
      }
      fermeParLOperateur.current = false;
      dejaOuvertes.current.add(id);
      setOpenId(id);
      // L'avis part APRÈS la sélection et avant le focus : un appelant qui
      // journalise l'ouverture doit la journaliser même si le panneau ne prend
      // jamais le focus — un navigateur qui refuserait le focus ne dispense
      // personne de la trace.
      const ligne = entries.find((entry) => entry.id === id);
      if (ligne !== undefined) onOpen?.(ligne);
      // **Le focus part sur le PANNEAU lui-même**, au tour de rendu suivant —
      // il n'existe pas encore au moment du clic.
      //
      // Il partait sur le bouton « Fermer », et c'était tenable tant que ce
      // bouton ouvrait l'en-tête : la tabulation descendait ensuite dans le
      // corps du dossier. Le dessin n'a pas de bouton dans son en-tête ; la
      // fermeture a rejoint les commandes, au pied. Laisser le focus s'y poser
      // aurait fait SAUTER tout le corps à la première tabulation — sur la
      // revue KYC, l'analyste aurait dû revenir en arrière pour atteindre les
      // motifs, un geste de plus par dossier.
      //
      // Le panneau porte donc `tabIndex={-1}` et prend le focus : la
      // tabulation suivante entre dans le corps, puis atteint les commandes.
      // C'est l'ordre de lecture, et c'est l'ordre du dessin.
      if (porterLeFocus) queueMicrotask(() => panneau.current?.focus());
    },
    [entries, onOpen, openId],
  );

  /**
   * **La première ligne s'ouvre d'elle-même** — le dessin ne montre jamais un
   * côté droit vide.
   *
   * Elle ne s'ouvre pas deux fois, ne vole pas le focus, et ne rouvre pas ce que
   * l'opérateur vient de refermer. Le dossier retenu est le **premier de la
   * file**, que le serveur a déjà ordonné par priorité puis par ancienneté :
   * c'est celui qu'il faut traiter d'abord, et le choisir ici serait refaire un
   * tri que la file porte déjà.
   */
  useEffect(() => {
    if (!openFirstByDefault || openId !== null || fermeParLOperateur.current) return;
    const premiere = entries[0];
    if (premiere === undefined || dejaOuvertes.current.has(premiere.id)) return;
    open(premiere.id, false);
  }, [entries, open, openFirstByDefault, openId]);

  const close = useCallback(() => {
    const revenir = openId;
    fermeParLOperateur.current = true;
    setOpenId(null);
    if (revenir !== null) rowRefs.current.get(revenir)?.focus();
  }, [openId]);

  const surTouche = useCallback(
    (evenement: KeyboardEvent<HTMLElement>, id: string) => {
      if (evenement.key === 'Enter') {
        evenement.preventDefault();
        open(id);
      } else if (evenement.key === 'Escape' && openId !== null) {
        evenement.preventDefault();
        close();
      }
    },
    [close, open, openId],
  );

  const ouverte = entries.find((entry) => entry.id === openId) ?? null;

  /**
   * Rend [decision] sur le dossier ouvert, referme, et porte le focus sur le
   * **dossier suivant**.
   */
  const decider = useCallback(
    (dossier: QueueEntry, decision: QueueDecision) => {
      const rang = entries.indexOf(dossier);
      const suivant = entries[rang + 1] ?? null;
      onDecide?.(dossier, decision.id);
      // Une commande qui ne dispose pas du dossier ne peut pas s'annoncer
      // « dossier suivant sélectionné » : ce serait annoncer un geste que la
      // coquille ne fait pas, dans la seule région que l'opérateur non voyant
      // écoute.
      const avance = decision.advances !== false;
      setAnnouncement(
        t(avance ? 'queue.decided' : 'queue.commandDone', {
          reference: dossier.reference,
          decision: t(decision.labelKey),
        }),
      );
      if (!avance) return;
      setOpenId(null);
      const cible = suivant ?? dossier;
      queueMicrotask(() => rowRefs.current.get(cible.id)?.focus());
    },
    [entries, onDecide, t],
  );

  /**
   * Les raccourcis, actifs **seulement quand un dossier est ouvert**.
   *
   * Une touche qui déciderait sans dossier ouvert déciderait sur le dossier
   * précédent — la faute la plus coûteuse que cet écran puisse commettre.
   */
  const surRaccourci = useCallback(
    (evenement: KeyboardEvent<HTMLElement>) => {
      // Un raccourci ne se déclenche pas pendant une saisie : l'analyste tape
      // un motif de rejet, il ne rejette pas à chaque lettre.
      const cible = evenement.target as HTMLElement;
      const nom = cible.tagName;
      if (nom === 'INPUT' || nom === 'TEXTAREA' || cible.isContentEditable) return false;
      if (evenement.altKey || evenement.ctrlKey || evenement.metaKey) return false;
      const touche = evenement.key.toLowerCase();
      // Une commande sans touche n'en capte aucune : comparer une chaîne vide
      // à `''` n'arrive jamais — `evenement.key` fait au moins un caractère —,
      // mais l'écrire ainsi le rend lisible plutôt que vrai par accident.
      const decision = decisions.find(
        (d) => d.shortcut !== undefined && d.shortcut.toLowerCase() === touche,
      );
      if (decision === undefined) return false;
      // **Le garde-fou unique.** Sans dossier ouvert, il n'y a rien à décider —
      // et se rabattre sur une ligne quelconque déciderait sur le mauvais
      // dossier, la faute la plus coûteuse que cet écran puisse commettre.
      const dossier = entries.find((entry) => entry.id === openId);
      if (dossier === undefined) return false;
      evenement.preventDefault();
      decider(dossier, decision);
      return true;
    },
    [decider, decisions, entries, openId],
  );

  return (
    <section
      className="queue"
      aria-label={t('queue.landmark')}
      onKeyDown={(evenement) => {
        if (evenement.key === 'Escape' && openId !== null) {
          evenement.preventDefault();
          close();
          return;
        }
        surRaccourci(evenement);
      }}
    >
      <p className="queue__hint">{t('queue.keyboardHint')}</p>
      {decisions.every((d) => d.shortcut === undefined) ? null : (
        <p className="queue__hint">
          {t('queue.decisionHint', {
            raccourcis: decisions
              .filter((d) => d.shortcut !== undefined)
              .map((d) => `${(d.shortcut as string).toUpperCase()} ${t(d.labelKey)}`)
              .join(' · '),
          })}
        </p>
      )}
      {/* La région vivante annonce la décision rendue. Sans elle, un opérateur
          non voyant appuierait deux fois sur la même touche. */}
      <p className="queue__status" role="status" aria-live="polite">
        {announcement}
      </p>

      <div className="queue__body">
        {/* **La carte de la table.** C'est elle qui porte le filet, le rayon et
            le fond — le dessin en fait une boîte fermée, table ET pied compris.
            Le `<table>` n'est plus la carte : il en est le contenu. */}
        <div className="queue__panel">
          <table className="queue__table">
            <thead>
              {table.getHeaderGroups().map((groupe) => (
                <tr key={groupe.id}>
                  {groupe.headers.map((entete) => (
                    <th key={entete.id} scope="col">
                      {flexRender(entete.column.columnDef.header, entete.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((ligne, index) => (
                <tr
                  key={ligne.id}
                  ref={(element) => {
                    if (element === null) rowRefs.current.delete(ligne.original.id);
                    else rowRefs.current.set(ligne.original.id, element);
                  }}
                  tabIndex={0}
                  aria-label={t('queue.rowLabel', { reference: ligne.original.reference })}
                  aria-posinset={index + 1}
                  aria-setsize={entries.length}
                  aria-expanded={openId === ligne.original.id}
                  aria-controls={openId === ligne.original.id ? detailId : undefined}
                  data-selected={openId === ligne.original.id ? 'true' : undefined}
                  className="queue__row"
                  onKeyDown={(evenement) => surTouche(evenement, ligne.original.id)}
                  onClick={() => open(ligne.original.id)}
                >
                  {ligne.getVisibleCells().map((cellule, rang) => (
                    <td key={cellule.id} data-emphasis={columns[rang]?.emphasis ?? 'plain'}>
                      {flexRender(cellule.column.columnDef.cell, cellule.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>

          {entries.length === 0 ? <p className="queue__empty">{t('queue.empty')}</p> : null}

          {footer === undefined ? null : <div className="queue__foot">{footer}</div>}
        </div>

        {ouverte === null ? null : (
          <aside
            className="queue__detail"
            id={detailId}
            aria-label={t('queue.detailLandmark')}
            ref={panneau}
            // Atteignable par le code, jamais par la tabulation : c'est la
            // touche Entrée sur la ligne qui l'ouvre, et une seconde halte au
            // Tab entre la file et le dossier n'aurait rien apporté.
            tabIndex={-1}
          >
            {/* **L'en-tête du dessin : un titre, une pastille. Rien d'autre.**
                Le livré y mettait l'identifiant technique de la ligne — `296`,
                la clé primaire — et un bouton « Fermer ». Ni l'un ni l'autre
                n'est dessiné : le dessin prend la PREMIÈRE cellule de la ligne
                comme titre et la DERNIÈRE comme pastille
                (`title: row.cells[0].text, tag: lastCell.text`), et il n'a pas
                de bouton de fermeture du tout — une ligne y est toujours
                sélectionnée.

                Le produit, lui, doit pouvoir refermer : le focus vit dans le
                panneau, et sans fermeture l'opérateur ne reviendrait jamais à
                la file. La fermeture est donc **conservée et déplacée** au pied
                du panneau, là où le dessin range ses commandes — c'est une
                commande, elle va avec les commandes. Échap la rend au clavier,
                et l'indication en tête de file le dit déjà. */}
            <header className="queue__detail-header">
              <h2 className="queue__detail-title">{ouverte.values[0] ?? ouverte.reference}</h2>
              {/* La pastille n'existe que s'il y a une DERNIÈRE cellule
                  distincte de la première : sur une file à une seule colonne,
                  elle répéterait le titre mot pour mot. */}
              {ouverte.values.length > 1 ? (
                <span className="queue__detail-badge">
                  {ouverte.values[ouverte.values.length - 1]}
                </span>
              ) : null}
            </header>

            <div className="queue__detail-body">
              {/* **Le RÉSUMÉ n'est pas une liste écrite à la main.** Le dessin
                  le bâtit `boCfg.head.map((h, i) => …)` : les libellés SONT les
                  en-têtes de la table, les valeurs SONT les cellules de la
                  ligne choisie. Bâti pareil, une colonne ajoutée à la table
                  apparaît au panneau sans que personne y pense — et une colonne
                  retirée en disparaît. C'est la seule écriture qui ne peut pas
                  dériver. */}
              <QueueDetailSection title={t('queue.summaryTitle')}>
                {columns.map((column, rang) => (
                  <QueueFact
                    key={column.id}
                    label={t(column.labelKey)}
                    testId={`queue-summary-${column.id}`}
                  >
                    {ouverte.fullValues?.[rang] ?? ouverte.values[rang] ?? ''}
                  </QueueFact>
                ))}
              </QueueDetailSection>
              {renderDetail?.(ouverte)}
            </div>

            <div className="queue__detail-actions">
              <button type="button" onClick={close} className="queue__close">
                {t('queue.close')}
              </button>
              {decisions.map((decision) => (
                <button
                  key={decision.id}
                  type="button"
                  className="queue__decision"
                  data-decision={decision.id}
                  onClick={() => decider(ouverte, decision)}
                >
                  {decision.shortcut === undefined
                    ? t(decision.labelKey)
                    : t('queue.decisionLabel', {
                        decision: t(decision.labelKey),
                        shortcut: decision.shortcut.toUpperCase(),
                      })}
                </button>
              ))}
            </div>
          </aside>
        )}
      </div>
    </section>
  );
}

/**
 * Une **section du panneau de détail**, telle que le dessin la mesure.
 *
 * Le gabarit du fichier `.dc.html` écrit, pour chaque section :
 * `padding:10px 14px; border-bottom:1px solid var(--bsub);
 * display:flex; flex-direction:column; gap:7px`, et son titre en
 * `font-size:10px; font-weight:700; letter-spacing:.9px;
 * text-transform:uppercase; color:var(--tt)`.
 *
 * Les 10 points du titre passent sous le **plancher de lecture** du système
 * (12) ; le plancher l'emporte, et la hiérarchie passe par la graisse, la
 * couleur tertiaire et l'interlettrage. C'est la conduite déjà tenue partout
 * ailleurs sur ces écrans, et `app.css` la redit à l'endroit de la règle.
 *
 * Elle vit dans la coquille, et non dans un écran : les cinq files du
 * back-office ouvrent le **même** panneau, et cinq copies auraient dérivé —
 * c'est exactement ce que le porteur a vu, un panneau qui ne ressemblait plus
 * au dessin sur deux écrans à la fois.
 */
export function QueueDetailSection({
  title,
  children,
}: {
  readonly title: string;
  readonly children: ReactNode;
}): ReactNode {
  return (
    <section className="queue__detail-section">
      <h3 className="queue__detail-section-title">{title}</h3>
      <dl className="queue__facts">{children}</dl>
    </section>
  );
}

/**
 * Une **ligne du panneau** : un libellé à gauche, une valeur à droite.
 *
 * Le dessin l'écrit `display:flex; align-items:baseline;
 * justify-content:space-between; gap:10px`, libellé en `var(--ts)` et valeur
 * en graisse 600, chiffres tabulaires, **`text-align:right`**. C'est cet
 * alignement à droite qui manquait le plus au livré : des valeurs collées à
 * gauche sous des libellés collés à gauche font deux colonnes de texte, là où
 * le dessin fait une paire.
 *
 * Le couple reste un `<dt>`/`<dd>` — un terme et sa définition, ce qu'un
 * lecteur d'écran annonce comme tel. Le `<div>` qui les enveloppe est permis
 * dans un `<dl>` et ne change aucun rôle : il porte la ligne.
 */
export function QueueFact({
  label,
  testId,
  children,
}: {
  readonly label: string;
  /** L'ancre d'un contrôle, quand il y en a une. Jamais du texte d'écran. */
  readonly testId?: string;
  readonly children: ReactNode;
}): ReactNode {
  return (
    <div className="queue__fact">
      <dt className="queue__fact-label">{label}</dt>
      <dd className="queue__fact-value" data-testid={testId}>
        {children}
      </dd>
    </div>
  );
}
